import type {Nullish} from 'shared/web';

import {calculateReportPercentage} from './calculateReportPercentage';

/** Allocate rounding differences only when the categories make up the whole base. */
export function calculateReportShares(amounts: Array<number | Nullish>, base?: number | null) {
  const values = amounts.map((amount) => amount ?? Number.NaN);
  const sum = values.reduce((total, value) => total + value, 0);
  const total = base === undefined ? sum : base;
  const percentages = values.map((value) => calculateReportPercentage(value, total));
  if (percentages.some((value) => value === null)) {
    return amounts.map(() => null);
  }

  const magnitude = values.reduce((result, value) => result + Math.abs(value), 0);
  const tolerance = Math.max(
    Math.abs(total ?? 0) * 1e-10,
    Number.EPSILON * values.length * magnitude
  );

  // API totals can include other item types. Keep their actual denominator in that case.
  if (sum === 0 || total === null || Math.abs(sum - total) > tolerance) {
    return percentages;
  }

  const points = values.map((value) => (value / total) * 10000);
  const rounded = points.map(Math.floor);
  const remainder = 10000 - rounded.reduce((result, value) => result + value, 0);
  const order = points
    .map((value, index) => ({index, fraction: value - Math.floor(value)}))
    .toSorted((left, right) => right.fraction - left.fraction || left.index - right.index);
  const additions = new Set(order.slice(0, remainder).map(({index}) => index));
  return rounded.map((value, index) => (value + (additions.has(index) ? 1 : 0)) / 100);
}
